import { Link } from "react-router-dom";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { socialIcon, socialLabel, normalizeUrl } from "@/lib/socials";
import FreeGuideSignup from "@/components/FreeGuideSignup";

export default function Footer() {
  const { info } = useStoreInfo();
  const socials = (info?.socials || []).filter((s) => s.url);
  return (
    <footer className="border-t border-border bg-card/40 mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 border-b border-border flex flex-col md:flex-row md:items-center gap-6 md:justify-between" data-testid="footer-free-guide">
        <div className="max-w-md">
          <p className="font-mono-label text-[10px] text-primary mb-2">Free · $100 Value</p>
          <p className="font-display text-2xl leading-tight">Get "Build Your Own Mycology Lab" free</p>
          <p className="text-sm text-muted-foreground mt-1">Join our list — confirm your email and we'll send the download.</p>
        </div>
        <div className="md:w-[360px] shrink-0"><FreeGuideSignup variant="compact" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <img src="/nc-logo.png" alt="NC Mycology" className="h-28 w-auto object-contain mb-4" />
          <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
            Clean, lab-prepared mushroom mycelium and gourmet cultivation supplies from North Carolina.
            Sterile, healthy, ready to colonize.
          </p>
        </div>
        <div>
          <p className="font-mono-label text-xs text-primary mb-4">Shop</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/shop" className="hover:text-foreground transition-colors">All Products</Link></li>
            <li><Link to="/shop?category=Liquid+Cultures" className="hover:text-foreground transition-colors">Liquid Cultures</Link></li>
            <li><Link to="/shop?category=Grow+Bags+%26+Buckets" className="hover:text-foreground transition-colors">Grow Bags & Buckets</Link></li>
            <li><Link to="/shop?category=Ebooks+%26+Guides" className="hover:text-foreground transition-colors">Ebooks & Guides</Link></li>
            <li><Link to="/blog" data-testid="footer-blog" className="hover:text-foreground transition-colors">Blog</Link></li>
            <li><Link to="/faq" data-testid="footer-faq" className="hover:text-foreground transition-colors">FAQ</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-foreground transition-colors">Contact</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-mono-label text-xs text-primary mb-4">Policies</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/refund-policy" data-testid="footer-refund" className="hover:text-foreground transition-colors">Refund Policy</Link></li>
            <li><Link to="/shipping-policy" data-testid="footer-shipping" className="hover:text-foreground transition-colors">Shipping Policy</Link></li>
            <li><Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-mono-label text-xs text-primary mb-4">Contact</p>
          <ul className="space-y-2 text-sm text-muted-foreground font-mono">
            <li>{info?.address || "North Carolina"}</li>
            {info?.email && (
              <li>
                <a href={`mailto:${info.email}`} data-testid="footer-email" className="hover:text-foreground transition-colors break-all">
                  {info.email}
                </a>
              </li>
            )}
            {info?.phone && (
              <li>
                <a href={`tel:${info.phone.replace(/[^0-9+]/g, "")}`} data-testid="footer-phone" className="hover:text-foreground transition-colors">
                  {info.phone}
                </a>
              </li>
            )}
            <li>nc-mycology.com</li>
          </ul>
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-5" data-testid="footer-socials">
              {socials.map((s, i) => (
                <a key={i} href={normalizeUrl(s.platform, s.url)} target="_blank" rel="noopener noreferrer"
                  aria-label={s.label || socialLabel(s.platform)} title={s.label || socialLabel(s.platform)}
                  data-testid={`footer-social-${s.platform}-${i}`}
                  className="w-9 h-9 flex items-center justify-center border border-border rounded-full text-muted-foreground hover:text-primary hover:border-primary transition-colors">
                  <i className={`${socialIcon(s.platform)} text-[15px]`} aria-hidden="true" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-border py-4 text-center" data-testid="footer-hero-discount">
        <p className="text-xs text-muted-foreground">
          <span className="font-mono-label text-[10px] text-primary mr-2">Thank you for your service</span>
          Military &amp; First Responders get <span className="text-foreground font-semibold">20% off</span> —{" "}
          {info?.email ? (
            <a href={`mailto:${info.email}?subject=Military%2FFirst%20Responder%20Discount`} className="text-primary hover:underline">email us</a>
          ) : (
            <Link to="/contact" className="text-primary hover:underline">contact us</Link>
          )}{" "}
          for your code.
        </p>
      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground font-mono">
        © {new Date().getFullYear()} NC Mycology LLC · All cultures lab-tested for purity
      </div>
    </footer>
  );
}
